/**
 * Cost input — a single floating-label field combining a currency selector and an amount input in
 * one bordered box, mirroring the PhoneInput dial-code + number pattern. The trigger shows the
 * compact ISO 4217 code while a transparent native `<select>` overlaid on it lists full names.
 */

import { FloatingField } from './floating-field.js';
import { IconSizes, MaterialIcon } from './MaterialIcon.js';

/** Common currencies for luxury travel destinations. */
const CURRENCIES = [
  { code: 'USD', label: 'US Dollar' },
  { code: 'EUR', label: 'Euro' },
  { code: 'GBP', label: 'British Pound' },
  { code: 'CHF', label: 'Swiss Franc' },
  { code: 'AED', label: 'UAE Dirham' },
  { code: 'AUD', label: 'Australian Dollar' },
  { code: 'BHD', label: 'Bahraini Dinar' },
  { code: 'BRL', label: 'Brazilian Real' },
  { code: 'BWP', label: 'Botswana Pula' },
  { code: 'CAD', label: 'Canadian Dollar' },
  { code: 'DKK', label: 'Danish Krone' },
  { code: 'EGP', label: 'Egyptian Pound' },
  { code: 'HKD', label: 'Hong Kong Dollar' },
  { code: 'IDR', label: 'Indonesian Rupiah' },
  { code: 'INR', label: 'Indian Rupee' },
  { code: 'JPY', label: 'Japanese Yen' },
  { code: 'KES', label: 'Kenyan Shilling' },
  { code: 'KWD', label: 'Kuwaiti Dinar' },
  { code: 'MAD', label: 'Moroccan Dirham' },
  { code: 'MUR', label: 'Mauritian Rupee' },
  { code: 'MXN', label: 'Mexican Peso' },
  { code: 'MYR', label: 'Malaysian Ringgit' },
  { code: 'NOK', label: 'Norwegian Krone' },
  { code: 'NZD', label: 'New Zealand Dollar' },
  { code: 'OMR', label: 'Omani Rial' },
  { code: 'QAR', label: 'Qatari Riyal' },
  { code: 'SAR', label: 'Saudi Riyal' },
  { code: 'SEK', label: 'Swedish Krona' },
  { code: 'SGD', label: 'Singapore Dollar' },
  { code: 'THB', label: 'Thai Baht' },
  { code: 'TRY', label: 'Turkish Lira' },
  { code: 'TZS', label: 'Tanzanian Shilling' },
  { code: 'ZAR', label: 'South African Rand' },
];

export interface CurrencyInputProps {
  amount: string;
  currency: string;
  onAmountChange: (amount: string) => void;
  onCurrencyChange: (currency: string) => void;
  id?: string;
  disabled?: boolean;
}

export function CurrencyInput({ amount, currency, onAmountChange, onCurrencyChange, id, disabled }: CurrencyInputProps) {
  return (
    <FloatingField label="Cost" htmlFor={id} filled={amount !== ''} forceFloat disabled={disabled}>
      <div className="flex h-12 items-stretch">
        {/* Currency selector: compact code + chevron with a transparent native <select> overlaid so the
            trigger stays narrow while the dropdown lists full currency names. */}
        <div className="relative flex items-center gap-0.5 pl-3 pr-2 pt-5 pb-1 text-base text-foreground">
          <span>{currency}</span>
          <MaterialIcon icon="expand_more" size={IconSizes.sm} className="opacity-50" />
          <select
            aria-label="Currency"
            value={currency}
            disabled={disabled}
            onChange={(e) => onCurrencyChange(e.target.value)}
            className="absolute inset-0 cursor-pointer opacity-0 disabled:cursor-not-allowed"
          >
            {CURRENCIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code} — {c.label}
              </option>
            ))}
          </select>
        </div>

        {/* Divider between selector and amount — sits in the value row, clear of the floated label. */}
        <div className="mt-5.5 mb-1.5 w-px shrink-0 bg-input" />

        <input
          id={id}
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={amount}
          disabled={disabled}
          placeholder="0.00"
          onChange={(e) => {
            if (/^\d*\.?\d{0,2}$/.test(e.target.value)) onAmountChange(e.target.value);
          }}
          className="h-full flex-1 border-0 bg-transparent px-3 pt-5 pb-1 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-0 disabled:cursor-not-allowed"
        />
      </div>
    </FloatingField>
  );
}
